import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { PortalConfig } from '@wr/shared';
import { ApiError, errorMessage, get } from '../../api.js';
import { Card, Notice, PageHead, formatDateTime } from '../../components/ui.js';
import { useSignedIn } from '../../session.js';

interface Count {
  records: number;
  files: number;
}

interface Report {
  lastRun: {
    ranAt: string;
    results: Record<string, Count | number>;
  } | null;
  retention: PortalConfig['retention'];
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/** Each rule in the order of the brief (section 11), in plain words. */
function rules(r: PortalConfig['retention']): [key: string, label: string, rule: string][] {
  return [
    [
      'contactsLeads',
      'Contacts and leads',
      `${r.contactsLeadsAfterWrapupDays} days after their event's wrap-up, or ${r.officeRecordsAfterPushDays} days after their last push without an event; only once in HubSpot`,
    ],
    [
      'deletedRecords',
      'Deleted contacts and leads',
      `${r.deletedRecordsDays} days after they were deleted in the portal or in HubSpot (the Deleted in HubSpot list)`,
    ],
    [
      'receipts',
      'Receipts',
      `${r.expensesVisibleDays} days after they were sent, with the photo and the PDF`,
    ],
    ['travel', 'Travel', `${r.travelAfterEventDays} days after the show, with the files`],
    [
      'packages',
      'Packages',
      `${r.packagesAfterExpiryDays} days after they expire, with their prospect page and activity`,
    ],
    ['roiTrash', 'Deleted ROI projects', `${r.roiTrashDays} days after they were deleted`],
    ['auditLog', 'Audit log', `${r.auditLogMonths} months`],
  ];
}

/**
 * Settings > Retention purges (round 2; admins who read the audit log):
 * every night the purges delete for good the rows and their files on
 * Spaces, never anything in HubSpot. The last run, rule by rule.
 */
export function Purges() {
  const { refresh } = useSignedIn();
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    get<Report>('/admin/purges')
      .then((r) => current && setReport(r))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else if (current) setError(errorMessage(err));
      });
    return () => {
      current = false;
    };
  }, [refresh]);

  const run = report?.lastRun;
  const kept = typeof run?.results.keptNotInHubSpot === 'number' ? run.results.keptNotInHubSpot : 0;
  return (
    <>
      <Link to="/settings" className="back">
        Settings
      </Link>
      <PageHead
        title="Retention purges"
        lede="Every night at 03:00 UTC, what has reached the end of its retention leaves the portal for good: the rows and their files. Nothing is ever deleted in HubSpot."
      />
      {error && <Notice kind="error">{error}</Notice>}
      {report && (
        <>
          <Card title={run ? `Last purge: ${formatDateTime(run.ranAt)}` : 'Last purge'}>
            {!run && <p className="note">Not run yet. It runs every night.</p>}
            {rules(report.retention).map(([key, label, rule]) => {
              const count = run?.results[key];
              return (
                <div key={key} className="purge-row">
                  <span>
                    <b>{label}</b>
                    <span className="note">{rule}</span>
                  </span>
                  {count && typeof count === 'object' && (
                    <span className="purge-n">
                      {plural(count.records, 'record')}
                      {count.files > 0 ? `, ${plural(count.files, 'file')}` : ''}
                    </span>
                  )}
                </div>
              );
            })}
            {kept > 0 && (
              <Notice kind="warn">
                {kept} {kept === 1 ? 'contact or lead' : 'contacts or leads'} past their time stay
                in the portal: they were never sent to HubSpot, so the portal holds their only copy.
                Document them so they can be sent.
              </Notice>
            )}
          </Card>
          <Card title="Backups">
            <p className="note">
              The database backups keep a copy of what was purged for 7 days more, then it is gone
              everywhere. Files deleted on Spaces have no copy.
            </p>
          </Card>
        </>
      )}
      {!report && !error && <div className="spinner" aria-label="Loading" />}
    </>
  );
}
